import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import {
  Avatar,
  Button,
  Skeleton,
  Tooltip,
  Typography,
  theme,
} from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faStar as faStarOutline,
  faUser,
} from '@awesome.me/kit-cf858715fd/icons/classic/regular';
import {
  faStar,
  faEnvelope,
  faBuildings,
  faCalendarDays,
  faClockFive,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import {
  GetProfileDetailsCardLocationAndScheduleQuery,
  GetProfileDetailsCardUserQuery,
} from 'generated/graphql';
import {
  DayKey,
  formatWorkingHoursRange,
  getPrimaryTimeFrame,
} from './formatWorkingHours';

type AccountUserResult = NonNullable<
  NonNullable<GetProfileDetailsCardUserQuery['getAccountUsersByIds']>[number]
>;
type UserByIdResult = NonNullable<
  GetProfileDetailsCardUserQuery['getUserById']
>;

export type ProfileDetailsCardUser = AccountUserResult & {
  timeZone: UserByIdResult['timeZone'];
  workingHours: UserByIdResult['workingHours'];
  defaultLocation: GetProfileDetailsCardLocationAndScheduleQuery['getLocationById'];
};

type ProfileDetailsCardProps = {
  user: ProfileDetailsCardUser;
  scheduledDays: DayKey[];
  isFavorite: boolean;
  onToggleFavorite: () => void;
  favoriteToggleDisabled?: boolean;
  showFavoriteToggle?: boolean;
};

export const ProfileDetailsCard = ({
  user,
  scheduledDays,
  isFavorite,
  onToggleFavorite,
  favoriteToggleDisabled = false,
  showFavoriteToggle = true,
}: ProfileDetailsCardProps): JSX.Element => {
  const { t } = useTranslation(['peopleProfileSidebar', 'days']);
  const { token } = theme.useToken();

  const favoriteLabel = isFavorite
    ? t('favoriteButton.remove')
    : t('favoriteButton.add');

  const titleAndDepartment = useMemo(() => {
    const parts = [user.title, user.department].filter(
      (value): value is string => !!value
    );
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [user.title, user.department]);

  const scheduleLabel = useMemo(
    () => scheduledDays.map((day) => t(day, { ns: 'days' })).join(', '),
    [scheduledDays, t]
  );
  const workingHoursLabel = useMemo(
    () =>
      formatWorkingHoursRange(
        getPrimaryTimeFrame(user.workingHours),
        user.timeZone
      ),
    [user.workingHours, user.timeZone]
  );

  return (
    <Card $borderColor={token.colorBorderSecondary}>
      <Header>
        <Avatar
          size={80}
          src={user.avatar ?? undefined}
          icon={<FontAwesomeIcon icon={faUser} />}
          alt=""
        />
        {showFavoriteToggle && (
          <Tooltip title={favoriteLabel}>
            <FavoriteButton
              type="text"
              aria-label={favoriteLabel}
              aria-pressed={isFavorite}
              disabled={favoriteToggleDisabled}
              onClick={onToggleFavorite}
              icon={
                <FontAwesomeIcon
                  icon={isFavorite ? faStar : faStarOutline}
                  color={token['magenta-8']}
                />
              }
            />
          </Tooltip>
        )}
      </Header>

      <NameBlock>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {user.name ?? t('unknownUser')}
        </Typography.Title>
        {titleAndDepartment && (
          <Typography.Text strong>{titleAndDepartment}</Typography.Text>
        )}
      </NameBlock>

      <DetailList>
        {user.primaryEmail?.email && (
          <DetailRow
            icon={faEnvelope}
            tooltip={t('iconTooltips.primaryEmail')}
            value={user.primaryEmail.email}
          />
        )}
        {user.defaultLocation?.name && (
          <DetailRow
            icon={faBuildings}
            tooltip={t('iconTooltips.preferredLocation')}
            value={user.defaultLocation.name}
          />
        )}
        {scheduleLabel && (
          <DetailRow
            icon={faCalendarDays}
            tooltip={t('iconTooltips.defaultSchedule')}
            value={scheduleLabel}
          />
        )}
        {workingHoursLabel && (
          <DetailRow
            icon={faClockFive}
            tooltip={t('iconTooltips.workingHours')}
            value={workingHoursLabel}
          />
        )}
      </DetailList>
    </Card>
  );
};

type DetailRowProps = {
  icon: IconDefinition;
  tooltip: string;
  value: string;
};

const DetailRow = ({ icon, tooltip, value }: DetailRowProps): JSX.Element => {
  const { token } = theme.useToken();
  return (
    <DetailRowWrapper>
      <Tooltip title={tooltip}>
        <DetailIconWrapper aria-label={tooltip} role="img">
          <FontAwesomeIcon icon={icon} color={token.colorIcon} />
        </DetailIconWrapper>
      </Tooltip>
      <DetailValue>{value}</DetailValue>
    </DetailRowWrapper>
  );
};

export const ProfileDetailsCardSkeleton = (): JSX.Element => {
  const { token } = theme.useToken();
  return (
    <Card
      $borderColor={token.colorBorderSecondary}
      data-testid="profile-details-card-skeleton"
    >
      <Header>
        <Skeleton.Avatar active size={80} />
      </Header>
      <NameBlock>
        <Skeleton
          active
          paragraph={{ rows: 1, width: ['60%'] }}
          title={{ width: '80%' }}
        />
      </NameBlock>
      <Skeleton active paragraph={{ rows: 4 }} title={false} />
    </Card>
  );
};

const Card = styled.div<{ $borderColor: string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 16px;
  padding: 16px;
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 8px;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
`;

const FavoriteButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailRowWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DetailIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  /* Align icon to the first line of wrapped text */
  height: 22px;
  flex-shrink: 0;
`;

const DetailValue = styled(Typography.Text)`
  word-break: break-word;
`;
